export const CLIP_CREATION_EPSILON = 0.001;

export const CLIP_COLORS = [
  '#4fc3f7',
  '#81c784',
  '#ff8a65',
  '#ba68c8',
  '#fff176',
  '#f48fb1',
  '#4db6ac',
  '#ffb74d',
  '#e57373',
  '#7986cb'
];

export function getClipRange(clip) {
  const markerTimes = (clip?.markers || [])
    .map(marker => marker?.time)
    .filter(time => typeof time === 'number' && Number.isFinite(time));

  if (markerTimes.length < 2) {
    return null;
  }

  return {
    start: Math.min(...markerTimes),
    end: Math.max(...markerTimes)
  };
}

export function rangesOverlap(a, b) {
  return a.start < b.end && b.start < a.end;
}

export function clipOverlapsAny({clip, clips = [], excludeClipId = null, excludeClip = null}) {
  const clipRange = getClipRange(clip);

  if (!clipRange) {
    return false;
  }

  return clips.some(existingClip => {
    if (
      !existingClip
      || existingClip === excludeClip
      || (excludeClipId !== null && existingClip.id === excludeClipId)
    ) {
      return false;
    }

    const existingRange = getClipRange(existingClip);
    return existingRange && rangesOverlap(clipRange, existingRange);
  });
}

export function findClipById({clipId, clips = []}) {
  if (clipId === null || clipId === undefined) {
    return null;
  }

  const normalizedClipId = String(clipId);

  return clips.find(clip => clip && String(clip.id) === normalizedClipId) || null;
}

export function findMarkerById({markerId, clipId = null, clips = []}) {
  if (markerId === null || markerId === undefined) {
    return null;
  }

  const normalizedMarkerId = String(markerId);
  const selectedClip = clipId === null || clipId === undefined
    ? null
    : findClipById({clipId, clips});
  const searchClips = selectedClip ? [selectedClip] : clips.filter(Boolean);

  for (const clip of searchClips) {
    const marker = (clip?.markers || []).find(item => item && String(item.id) === normalizedMarkerId);
    if (marker) {
      return {
        clip,
        marker
      };
    }
  }

  return null;
}

export function getClipDeltaBounds({clip, clips = [], minTime = 0, maxTime = Infinity}) {
  const clipRange = getClipRange(clip);
  if (!clipRange) {
    return null;
  }

  let minDelta = minTime - clipRange.start;
  let maxDelta = maxTime - clipRange.end;

  for (const otherClip of clips) {
    if (!otherClip || otherClip === clip || (clip?.id && otherClip.id === clip.id)) {
      continue;
    }

    const otherRange = getClipRange(otherClip);
    if (!otherRange) {
      continue;
    }

    if (otherRange.end <= clipRange.start) {
      minDelta = Math.max(minDelta, otherRange.end - clipRange.start);
      continue;
    }

    if (otherRange.start >= clipRange.end) {
      maxDelta = Math.min(maxDelta, otherRange.start - clipRange.end);
    }
  }

  return {
    minDelta,
    maxDelta
  };
}

export function isTimeInsideExistingClip(time, clips = []) {
  return clips.some(clip => {
    const range = getClipRange(clip);
    if (!range) {
      return false;
    }

    return time > range.start && time < range.end;
  });
}

export function getNextClipStartBoundary({time, clips = [], maxTime = Infinity}) {
  let boundary = Number.isFinite(maxTime) ? maxTime : Infinity;

  for (const clip of clips) {
    const range = getClipRange(clip);
    if (!range) {
      continue;
    }

    if (range.start >= time) {
      boundary = Math.min(boundary, range.start);
    }
  }

  return boundary;
}

export function getVideoSrcFromFile(file) {
  if (!file) {
    return null;
  }

  return file.preview_url || file.download_items?.[0]?.path || null;
}

export function getFileDurationSeconds(file) {
  const durationSeconds = Number(file?.duration_seconds ?? file?.durationSeconds ?? file?.video_duration_seconds);
  if (Number.isFinite(durationSeconds) && durationSeconds > 0) {
    return durationSeconds;
  }

  const durationMs = Number(file?.duration ?? file?.duration_ms ?? file?.durationMs);
  if (Number.isFinite(durationMs) && durationMs > 0) {
    return durationMs / 1000;
  }

  return 0;
}

export function getFileNameWithoutExtension(file) {
  const rawFileName = file?.name || file?.file_name || '';
  const normalizedFileName = String(rawFileName)
    .split(/[\\/]/)
    .pop()
    ?.split('?')[0]
    ?.split('#')[0]
    ?.trim() || '';

  if (!normalizedFileName) {
    return '';
  }

  const extensionIndex = normalizedFileName.lastIndexOf('.');
  if (extensionIndex <= 0) {
    return normalizedFileName;
  }

  return normalizedFileName.slice(0, extensionIndex);
}

export function getDefaultClipName({file, clipNumber}) {
  const normalizedClipNumber = Number.isFinite(Number(clipNumber)) ? Number(clipNumber) : 1;
  const baseName = getFileNameWithoutExtension(file) || 'Clip';
  const suffix = `_${normalizedClipNumber}`;
  const truncatedBaseName = baseName.slice(0, Math.max(0, VIDEO_EDITOR_CLIP_NAME_MAX_LENGTH - suffix.length));

  return `${truncatedBaseName}${suffix}`;
}

export function getMarkerRequestKey(marker) {
  if (!marker?.id) {
    return null;
  }

  return String(marker.id);
}

export function getClipRequestKey(clip) {
  if (!clip?.id) {
    return null;
  }

  return String(clip.id);
}

export function getCustomFrameRequestKey(frame) {
  if (!frame?.design_project_id) {
    return null;
  }

  return String(frame.design_project_id);
}

let _localEntityIdCounter = 0;

export function nextLocalEntityId(prefix = 'video-editor') {
  _localEntityIdCounter += 1;
  return `${prefix}-${Date.now()}-${_localEntityIdCounter}`;
}
